// /en/mfa-legalisation/documents/$group — Wave 63: EN deep-dive per document family
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import type { MfaDocGroup } from "@/data/mfa-doc-groups";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/mfa-legalisation/documents/$group")({
  loader: async ({ params }) => {
    const { MFA_DOC_GROUPS, getMfaDocGroup } = await import("@/data/mfa-doc-groups");
    const group = getMfaDocGroup(params.group);
    if (!group) throw notFound();
    return {
      __d: { MFA_DOC_GROUPS, getMfaDocGroup }, group };
  },
  head: ({ params, loaderData }) => {
    const g = loaderData?.group as MfaDocGroup | undefined;
    if (!g) return {};
    const path = `/en/mfa-legalisation/documents/${params.group}`;
    const url = `${SITE}${path}`;
    const thUrl = `${SITE}/mfa-legalisation/documents/${params.group}`;
    const title = `${g.titleEn} — MFA & Embassy Legalisation in Thailand | NYC`;
    const desc = g.summaryEn.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, path, "en") },
        { name: "description", content: ctaDesc(desc, path, "en") },
        { property: "og:title", content: ctaTitle(title, path, "en") },
        { property: "og:description", content: ctaDesc(desc, path, "en") },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: thUrl },
        { rel: "alternate", hrefLang: "x-default", href: thUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "MFA + Embassy Legalisation",
                item: `${SITE}/en/mfa-legalisation`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Documents we legalise",
                item: `${SITE}/en/mfa-legalisation/documents`,
              },
              { "@type": "ListItem", position: 3, name: g.titleEn, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            inLanguage: "en",
            mainEntity: g.faqsEn.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: MfaDocGroupEnPage,
});

function MfaDocGroupEnPage() {
  const { __d: { MFA_DOC_GROUPS, getMfaDocGroup } } = Route.useLoaderData() as { __d: typeof import("@/data/mfa-doc-groups") };
  const { group: g } = Route.useLoaderData() as { group: MfaDocGroup };
  const others = MFA_DOC_GROUPS.filter((x) => x.slug !== g.slug);

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/en/mfa-legalisation" className="hover:underline">
          MFA + Embassy Legalisation
        </Link>{" "}
        /{" "}
        <Link to="/en/mfa-legalisation/documents" className="hover:underline">
          Documents we legalise
        </Link>{" "}
        / {g.titleEn}
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{g.titleEn}</h1>
      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        {g.summaryEn}
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>Thailand's Apostille status:</strong> the Convention enters into force for Thailand on{" "}
        {APOSTILLE_STATUS.entryIntoForceEn}. Until then, documents still pass through the Department of Consular Affairs
        and the destination embassy.{" "}
        <a href={APOSTILLE_STATUS.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="underline">
          Source: HCCH
        </a>
      </aside>

      <div className="mt-8 space-y-4 text-base leading-8">
        {g.bodyEn.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <h2 className="mt-10 text-2xl font-semibold tracking-tight">Documents in this family</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.documentsEn.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">Order of steps</h2>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-7">
        {g.chainEn.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">Preparation checklist</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.checklistEn.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">FAQs</h2>
      <div className="mt-2 space-y-3">
        {g.faqsEn.map((f) => (
          <div key={f.q} className="rounded-lg border border-border p-4">
            <p className="font-medium voice-faq-q">{f.q}</p>
            <p className="mt-1 text-sm leading-7 text-muted-foreground voice-faq-a">{f.a}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-10 text-xl font-semibold tracking-tight">Other document families</h2>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
        {others.map((o) => (
          <li key={o.slug}>
            <Link
              to="/en/mfa-legalisation/documents/$group"
              params={{ group: o.slug }}
              className="text-primary hover:underline"
            >
              {o.titleEn}
            </Link>
          </li>
        ))}
      </ul>
          <ServiceAuthorityBlock topic="mfa" locale="en" />
    </main>
  );
}
